import { forwardRef, type SelectHTMLAttributes } from "react";
import { cn } from "./cn";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  options: SelectOption[];
  /** Shown as the first entry when the value is empty. */
  placeholder?: string;
  invalid?: boolean;
}

/**
 * A single-choice control, styled to match `Input`.
 *
 * Native `<select>` rather than a custom listbox: it gets keyboard handling,
 * type-ahead and the platform's own picker on mobile for free, and none of the
 * pickers in this suite need multi-line options or icons.
 *
 * A value that isn't in `options` is rendered as an extra disabled entry rather
 * than silently showing the first option — data that no longer matches its
 * reference list should be visible, not quietly rewritten.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, invalid, className, value, ...rest },
  ref,
) {
  const current = value === null || value === undefined ? "" : String(value);
  const missing = current !== "" && !options.some((option) => option.value === current);

  return (
    <select
      ref={ref}
      value={current}
      aria-invalid={invalid || undefined}
      className={cn(
        "h-9 w-full rounded-md border border-line bg-surface px-3 text-sm text-ink",
        "focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent",
        "disabled:cursor-not-allowed disabled:opacity-60",
        invalid && "border-risk focus-visible:outline-risk",
        "max-md:h-11",
        className,
      )}
      {...rest}
    >
      {(placeholder !== undefined || current === "") && !options.some((option) => option.value === "") && (
        <option value="">{placeholder ?? ""}</option>
      )}
      {missing && (
        <option value={current} disabled>
          {current} (unknown)
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
});

/** Builds options from any `{ id, name }` reference list. */
export function entityOptions(
  entities: Array<{ id: number; name?: string | null }> | undefined,
): SelectOption[] {
  return (entities ?? [])
    .map((entity) => ({ value: String(entity.id), label: entity.name ?? `#${entity.id}` }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Builds options from an enum-like list of constant names. */
export function enumOptions(values: readonly string[]): SelectOption[] {
  return values.map((value) => ({ value, label: value }));
}
